import { cartMoney } from './cartItem';
import type { DeliveryQuote, DeliverySelection } from './deliveryQuote';

const normalize = (value: unknown) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
const number = (value: unknown) => {
  if ((typeof value !== 'number' && typeof value !== 'string') || !String(value).trim()) return NaN;
  return Number(String(value).replace(',', '.'));
};
export const parseDeliveryMoney = (value: unknown) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || !value.trim()) return NaN;
  const text = value.trim();
  return Number(text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text);
};

// O contrato publicado fornece as configurações na empresa; não fornece `entrega` no carrinho.
// A cotação compatível usa sempre uma empresa consultada agora, nunca o frete salvo no navegador.
export function quoteFromCurrentCompany(company: any, selection: DeliverySelection, validateHours: boolean, now = new Date()): DeliveryQuote {
  if (!selection.endereco?.trim()) throw new Error('Informe o endereço ou selecione retirada.');
  if (validateHours) validateDeliverySchedule(company, selection.agendamento, now);
  const configurations: any[] = Array.isArray(company.configuracoesEntrega) ? company.configuracoesEntrega
    : Object.values(company.configuracoesEntrega || {});
  const validConfigurations = configurations.filter(config => config && typeof config === 'object');
  let taxa: unknown, tempo: unknown;
  if (selection.endereco === 'RETIRADA') {
    taxa = 0;
    const times = validConfigurations.map(config => number(config.tempo)).filter(value => Number.isFinite(value) && value >= 0);
    tempo = times.length ? String(Math.min(...times)) : '';
  } else {
    const type = normalize(company.tipoConfiguracoesEntrega) || (validConfigurations.some(config => config.raio !== undefined) ? 'raio' : 'bairro');
    let configuration;
    if (type.includes('raio')) {
      const latitude = number(selection.latitude), longitude = number(selection.longitude);
      const storeLat = number(company.empresaLatitude), storeLng = number(company.empresaLongitude);
      if (![latitude, longitude, storeLat, storeLng].every(Number.isFinite)
        || Math.abs(latitude) > 90 || Math.abs(storeLat) > 90 || Math.abs(longitude) > 180 || Math.abs(storeLng) > 180) {
        throw new Error('Selecione um endereço com localização válida.');
      }
      const radians = (angle: number) => angle * Math.PI / 180;
      const a = Math.sin(radians(storeLat - latitude) / 2) ** 2
        + Math.cos(radians(latitude)) * Math.cos(radians(storeLat)) * Math.sin(radians(storeLng - longitude) / 2) ** 2;
      const distance = 6371 * 2 * Math.atan2(Math.sqrt(Math.min(1, a)), Math.sqrt(Math.max(0, 1 - a)));
      configuration = [...validConfigurations].filter(config => Number.isFinite(number(config.raio)))
        .sort((first, second) => number(first.raio) - number(second.raio)).find(config => distance <= number(config.raio));
    } else if (type.includes('bairro')) {
      const neighborhood = normalize(selection.bairro);
      const addressParts = normalize(selection.endereco).split(',').map(part => part.trim());
      const matches = validConfigurations.filter(config => normalize(config.bairro)
        && (neighborhood ? normalize(config.bairro) === neighborhood : addressParts.includes(normalize(config.bairro))));
      if (matches.length === 1) configuration = matches[0];
      else throw new Error('Selecione novamente o bairro para confirmar o frete atual.');
    }
    if (!configuration) throw new Error('Este endereço está fora da área de entrega atual da loja. Selecione outro endereço ou retirada.');
    taxa = configuration.taxa; tempo = configuration.tempo;
  }
  const fee = parseDeliveryMoney(taxa);
  if (!Number.isFinite(fee) || fee < 0) throw new Error('Não foi possível confirmar a taxa de entrega deste endereço.');
  return { empresaid: company.pk, taxa: cartMoney(fee), tempo: String(tempo ?? ''), consultadoEm: now.toISOString() };
}

export function validateDeliverySchedule(company: any, schedule: DeliverySelection['agendamento'], now = new Date()) {
  let date = now;
  if (schedule) {
    const day = String(schedule.data || '').match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    const hour = String(schedule.hora || '').match(/(?:T|^)([0-2]\d:[0-5]\d)/)?.[1];
    if (!day || !hour || Number(hour.slice(0, 2)) > 23) throw new Error('Informe uma data e horário válidos para o agendamento.');
    date = new Date(`${day}T${hour}:00-03:00`);
    if (!Number.isFinite(date.getTime()) || company.agendamento !== true || date.getTime() < Math.floor(now.getTime() / 60000) * 60000) {
      throw new Error('O agendamento não está disponível. Escolha outro horário.');
    }
  }
  const values = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Sao_Paulo',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(date).map(part => [part.type, part.value]));
  if (schedule && `${values.year}-${values.month}-${values.day}` !== String(schedule.data).slice(0, 10)) throw new Error('Informe uma data válida para o agendamento.');
  const day = new Date(`${values.year}-${values.month}-${values.day}T12:00:00Z`).getUTCDay();
  const minute = Number(values.hour) * 60 + Number(values.minute);
  const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const open = (Array.isArray(company.horarios) ? company.horarios : []).some((range: any) => {
    if (!/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(range.start)) return false;
    const [hour, min] = range.start.split(':').map(Number), start = hour * 60 + min, duration = Number(range.duration);
    return Number.isFinite(duration) && duration > 0 && ((range.dayOfWeek === days[day] && minute >= start && minute <= start + duration)
      || (range.dayOfWeek === days[(day + 6) % 7] && start + duration >= 1440 && minute <= start + duration - 1440));
  });
  if (company.deliveryFechado === true || company.bloquearAcesso === true || !open) {
    throw new Error(schedule ? 'A loja não está disponível no horário agendado. Escolha outro horário.' : 'A loja não está disponível para pedidos agora.');
  }
}
